import {FC, useEffect, useState} from 'react';
import {Box, InputAdornment, TextField, Typography} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {useIntl} from 'react-intl';
import {reportServiceApi} from 'src/api';
import {ConsumptionRequest} from 'src/types';
import {BreakdownRow, BreakdownTable} from '../BreakdownTable';
import {tabsSx} from './BreakdownTabs.sx';
import {useDebouncedValue} from './useDebouncedValue';

export type BreakdownTabKey =
    | 'locations'
    | 'cities'
    | 'chargers'
    | 'users'
    | 'companies'
    | 'realEstates'
    | 'businesses';

interface BreakdownTabsProps {
    baseRequest: ConsumptionRequest;
    availableTabs: BreakdownTabKey[];
}

const DEFAULT_SIZE = 10;

const SEARCH_UNAVAILABLE: Set<BreakdownTabKey> = new Set<BreakdownTabKey>(['businesses']);

interface TabInnerProps {
    req: ConsumptionRequest;
    keyword: string;
    supportsSearch: boolean;
}

// Reset page to 0 whenever keyword or size changes
const usePaginationState = (keyword: string) => {
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(DEFAULT_SIZE);

    useEffect(() => { setPage(0); }, [keyword]);

    const handleSizeChange = (newSize: number) => {
        setSize(newSize);
        setPage(0);
    };

    return {page, size, setPage, handleSizeChange};
};

const buildReq = (req: ConsumptionRequest, keyword: string, supportsSearch: boolean, page: number, size: number): ConsumptionRequest => ({
    ...req,
    ...(supportsSearch && keyword.trim() ? {keyword: keyword.trim()} : {}),
    page,
    size,
});

const LocationsTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByLocationsQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: String(s.locationId), name: s.locationName, ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

const CitiesTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByCitiesQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: s.city, name: s.city, ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

const ChargersTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByChargersQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: String(s.chargerId),
        name: s.chargerName || s.chargePointId,
        subtitle: s.chargerName ? s.chargePointId : undefined,
        ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

const UsersTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByUsersQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: String(s.userId),
        name: s.userFullName,
        subtitle: s.userEmail,
        ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

const CompaniesTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByCompaniesQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: String(s.companyId), name: s.companyName, ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

const RealEstatesTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByRealEstatesQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: String(s.realEstateId), name: s.realEstateName, ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

const BusinessesTab: FC<TabInnerProps> = ({req, keyword, supportsSearch}) => {
    const {page, size, setPage, handleSizeChange} = usePaginationState(keyword);
    const {data, isFetching} = reportServiceApi.useGetConsumptionByBusinessesQuery(
        buildReq(req, keyword, supportsSearch, page, size)
    );
    const rows: BreakdownRow[] = data?.statistics?.map((s) => ({
        id: String(s.businessId), name: s.businessName, ...s,
    })) ?? [];
    const max = Math.max(...rows.map((r) => r.consumption), 1);
    return (
        <BreakdownTable
            rows={rows} loading={isFetching} maxConsumption={max}
            page={page} size={size} totalElements={data?.totalElements ?? rows.length}
            onPageChange={setPage} onSizeChange={handleSizeChange}
        />
    );
};

export const BreakdownTabs: FC<BreakdownTabsProps> = ({baseRequest, availableTabs}) => {
    const intl = useIntl();
    const [activeTab, setActiveTab] = useState<BreakdownTabKey>(availableTabs[0]);
    const [searchInput, setSearchInput] = useState('');
    const debouncedSearch = useDebouncedValue(searchInput, 300);

    useEffect(() => {
        setSearchInput('');
    }, [activeTab]);

    const supportsSearch = !SEARCH_UNAVAILABLE.has(activeTab);

    const tabLabels: Record<BreakdownTabKey, string> = {
        locations: intl.formatMessage({id: 'ecByLocation'}),
        cities: intl.formatMessage({id: 'ecByCity'}),
        chargers: intl.formatMessage({id: 'ecByCharger'}),
        users: intl.formatMessage({id: 'ecByUser'}),
        companies: intl.formatMessage({id: 'ecByCompany'}),
        realEstates: intl.formatMessage({id: 'ecByRealEstate'}),
        businesses: intl.formatMessage({id: 'ecByBusiness'}),
    };

    const renderActive = () => {
        const props: TabInnerProps = {
            req: baseRequest,
            keyword: debouncedSearch,
            supportsSearch,
        };
        switch (activeTab) {
            case 'locations': return <LocationsTab {...props} />;
            case 'cities': return <CitiesTab {...props} />;
            case 'chargers': return <ChargersTab {...props} />;
            case 'users': return <UsersTab {...props} />;
            case 'companies': return <CompaniesTab {...props} />;
            case 'realEstates': return <RealEstatesTab {...props} />;
            case 'businesses': return <BusinessesTab {...props} />;
        }
    };

    return (
        <Box sx={tabsSx.root}>
            <Box sx={tabsSx.tabsHeader}>
                <Box sx={tabsSx.tabsGroup}>
                    {availableTabs.map((key) => (
                        <Typography
                            key={key}
                            sx={activeTab === key ? tabsSx.tabActive : tabsSx.tab}
                            onClick={() => setActiveTab(key)}
                        >
                            {tabLabels[key]}
                        </Typography>
                    ))}
                </Box>
                {supportsSearch && (
                    <TextField
                        size="small"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={intl.formatMessage({id: 'search'})}
                        sx={tabsSx.search}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{fontSize: 18, color: 'rgba(0,0,0,0.4)'}} />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
            </Box>
            <Box sx={tabsSx.content}>{renderActive()}</Box>
        </Box>
    );
};
